import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { map } from 'rxjs/operators';
import { ButtonComponent } from '../button/button.component';
import { TextareaFieldComponent } from '../textarea-field/textarea-field.component';

/**
 * Reopen Request Form Component
 *
 * Asks the user why a closed record should be reopened. Purely presentational:
 * it owns the reason field and its validation, and reports the outcome through
 * `submitted` / `cancelled` — the consumer performs the actual request.
 *
 * @example
 * ```html
 * <cm-reopen-request-form
 *   [submitting]="requestingReopen()"
 *   (submitted)="onSubmitReopen($event)"
 *   (cancelled)="onCancelReopen()"
 * />
 * ```
 */
@Component({
  selector: 'cm-reopen-request-form',
  standalone: true,
  imports: [ReactiveFormsModule, ButtonComponent, TextareaFieldComponent],
  templateUrl: './reopen-request-form.component.html',
  styleUrl: './reopen-request-form.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReopenRequestFormComponent {
  /** Maximum reason length, matching the field attribute and the validator. */
  public static readonly MAX_LENGTH = 2000;

  public readonly label = input<string>('Reason for reopening');
  public readonly placeholder = input<string>('Describe what still needs attention...');
  public readonly submitLabel = input<string>('Submit request');
  public readonly cancelLabel = input<string>('Cancel');
  public readonly rows = input<number>(5);
  public readonly autId = input<string>('reopen-request-form');

  /** Blocks both actions and shows the spinner while the request is in flight. */
  public readonly submitting = input<boolean>(false);

  /** Emits the trimmed reason when the user submits. */
  public readonly submitted = output<string>();

  /** Emits when the user cancels. */
  public readonly cancelled = output<void>();

  public readonly maxLength = ReopenRequestFormComponent.MAX_LENGTH;

  /**
   * The visible label is rendered by this component, so the field needs its own
   * accessible name. It carries the requirement, which would otherwise be lost with
   * the native `required` attribute gone.
   */
  public readonly fieldAriaLabel = computed(() => `${this.label()} (required)`);

  public readonly reasonControl = new FormControl('', [
    Validators.required,
    Validators.maxLength(ReopenRequestFormComponent.MAX_LENGTH),
  ]);

  private readonly _reasonValid = toSignal(
    this.reasonControl.statusChanges.pipe(map(() => this.reasonControl.valid)),
    { initialValue: false },
  );

  public readonly canSubmit = computed(() => this._reasonValid() && !this.submitting());

  public onSubmit(): void {
    const reason = this.reasonControl.value?.trim();
    if (!reason) {
      return;
    }
    this.submitted.emit(reason);
  }

  public onCancel(): void {
    this.reasonControl.reset();
    this.cancelled.emit();
  }

  /** Clears the field, e.g. after the consumer's request succeeds. */
  public reset(): void {
    this.reasonControl.reset();
  }
}
